import {
  addDoc,
  collection,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { toStoredScreens } from '@racedash/shared'
import type { DashboardVisibility, ScreenEntry } from '@racedash/shared'
import { db } from '../lib/firebase'

const dashboards = () => collection(db, 'dashboards')
const dashboard = (id: string) => doc(db, 'dashboards', id)

/**
 * New dashboards are visible to the organisation.
 *
 * This follows from what the setting means. It is an audience, not a lifecycle
 * stage — so in a tool people use together, the ordinary case is that your
 * organisation can see your work, and "only me" is the deliberate exception.
 * (It also has to be this way round for share links to be creatable without an
 * unexplained extra step, since a link requires the organisation to see it.)
 */
export async function createDashboard(
  orgId: string,
  uid: string,
  name: string,
  screens: ScreenEntry[],
): Promise<string> {
  const ref = await addDoc(dashboards(), {
    orgId,
    createdBy: uid,
    name: name.trim() || 'Untitled dashboard',
    visibility: 'organisation' satisfies DashboardVisibility,
    // Written, not defaulted at read time. A dashboard that looks populated
    // while its document is empty cannot report whether it has unsaved changes,
    // because there is nothing to compare against.
    screens: toStoredScreens(screens),
    // Unbound until someone says which event it is for. Empty rather than
    // absent, so the field's shape is settled from the start.
    source: {},
    // Left empty deliberately. It is the pre-screens single grid, kept only so
    // older documents still open; a new dashboard has no use for it.
    layout: {},
    deleted: false,
    deletedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function renameDashboard(id: string, name: string): Promise<void> {
  await updateDoc(dashboard(id), {
    name: name.trim() || 'Untitled dashboard',
    updatedAt: serverTimestamp(),
  })
}

/**
 * Pin the car this dashboard is about, or clear it.
 *
 * On the document rather than in the URL, unlike the rest of the selection,
 * because it is a statement about what the dashboard is FOR — which race it
 * should open on, and which car its widgets mean by "our car" — rather than
 * about what one person is looking at this minute.
 */
export async function saveDefaultCar(id: string, car: string | null): Promise<void> {
  await updateDoc(dashboard(id), {
    defaultCar: car ?? deleteField(),
    updatedAt: serverTimestamp(),
  })
}

export async function setVisibility(id: string, visibility: DashboardVisibility): Promise<void> {
  await updateDoc(dashboard(id), { visibility, updatedAt: serverTimestamp() })
}

/**
 * Soft delete. The rules forbid hard deletes from any client, so this is an
 * update rather than a delete, and is always reversible by restore() below.
 */
export async function softDeleteDashboard(id: string): Promise<void> {
  await updateDoc(dashboard(id), {
    deleted: true,
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

export async function restoreDashboard(id: string): Promise<void> {
  await updateDoc(dashboard(id), {
    deleted: false,
    deletedAt: null,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Persist a dashboard's screens.
 *
 * Written through toStoredScreens rather than as-is, which does two things a
 * spread would not: it drops `undefined` fields, which Firestore rejects
 * outright — a screen carries `car: undefined` straight out of parsing, so this
 * would fail the whole save rather than merely lose a field — and it writes only
 * the fields we own.
 */
export async function saveScreens(id: string, screens: readonly ScreenEntry[]): Promise<void> {
  await updateDoc(dashboard(id), {
    screens: toStoredScreens(screens),
    updatedAt: serverTimestamp(),
  })
}
